import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { push } from 'connected-react-router'
import { useEffect, useMemo, useRef } from 'react'
import {
  putLabels,
  putTasksAssignee,
  putTasksStatus,
  putTasksPriority,
  getTaskMeta,
  getTaskData,
  postTaskComment,
  deleteTaskComment,
  putLabelsBulkUpdate,
  putTaskDetails,
  getTaskDetailsOnly,
  putTasksUpdate,
  putTasksCommentStatus,
  getAction,
  putTasksSignOffDetails,
  bulkFetchTasks,
  bulkFetchTaskComments,
} from 'actionHub/utils/actionHubApi'
import {
  actionHubFetchingTaskData,
  actionHubFetchingBulkTaskData,
  actionHubFetchedTaskData,
  actionHubUpdateTaskLabelValue,
  submittingTaskLabels,
  submittedTaskLabels,
  validateTaskLabel,
  submittedTaskAssignee,
  submittedTaskStatus,
  submittedTaskPriority,
  actionHubFetchedTaskMeta,
  actionHubFetchedTaskDataNoData,
  actionHubBulkFetchedTaskDataNoData,
  submittingTaskComment,
  submittedTaskComment,
  submittingTaskLabelsFailed,
  actionHubUpdateSelectedTasks,
  actionHubFetchSelectedTasks,
  actionHubSelectedFetchedCheck,
  submittedTaskActionState,
  actionHubUpdateTaskDetails,
  actionHubUpdatingTaskDetails,
  updateIsCommentRequired,
  updateIsCommentReceived,
  submittingTaskCommentFailed,
  submittedTaskCommentStatus,
  actionHubRemoveTask,
  actionHubHomeTaskMetaAction,
  updateSignOffDetails,
  resetTaskLabelsToDefault,
  actionHubUpdateBulkTasks,
  actionHubFetchedBulkTaskComments,
  actionHubUpdateMultipleTaskLabelValues,
} from 'actionHub/redux/actions'
import { MODAL_BULK_ACTION_UPDATES } from 'constants/forms'
import useApp from 'hooks/useApp'
import { modalHide } from 'actions/modals'
import { updateJobDownload } from 'actions/job'
import { downloadStatuses } from 'utils/business/jobs'
import { putUpdateDownloadStatus, getUserById } from 'utils/api/job'
import { notify } from 'actions/app'
import useAction from './useAction'

import {
  actionHubFetchingCommentAuthors,
  actionHubFetchedCommentAuthor,
} from '../redux/actions'

export default function useTask(actionSetId, taskId, taskIds) {
  const { updateGlobalFilters } = useAction(actionSetId)
  const dispatch = useDispatch()
  const { showNotification } = useApp()

  const isBulkUpdateLoading = useSelector(state => state.actionHub._isSelectedTasksUpdating)
  const bulkUpdateController = useRef(null)
  const isSubmittingLabels = useSelector(state => state.actionHub.tasks._isSubmittingLabels)
  const jobId = useSelector(state => state.actionHub.actionSet?.jobId)
  const task = useSelector(state => state.actionHub.tasks[taskId])
  const tasks = useSelector(state => (taskIds ? taskIds.map(id => state.actionHub.tasks[id]) : state.actionHub.tasks[taskId]))
  const allTasks = useSelector(state => state.actionHub.tasks)
  const taskData = useSelector(state => (task && task.dataIds ? task.dataIds.map(id => state.actionHub.data[id].data) : []))
  const { isChildDataLevel } = useSelector(state => state.actionHub.dataLevel)
  const bulkUpdateModalOpenStatus = useSelector(state => state?.modals?.[MODAL_BULK_ACTION_UPDATES]?.show)
  const {
    displayColumns: columns, labels, idColumns, meta, allowAttachments,
  } = useSelector(state => state.actionHub.dataLevel)
  const {
    idColumns: taskIdColumns,
    displayColumns: taskDisplayColumns,
    displayDetailColumns,
    allowComments,
  } = useSelector(state => state.actionHub.taskLevel)
  const selectedTasks = useSelector(state => state.actionHub.selectedTasks)
  const currentJob = useSelector(state => state.job)
  const isUserExternal = useSelector(state => state.app.user.isExternal)

  const mergedColumns = [
    ...columns.map(x => ({ ...x, idColumn: idColumns.includes(x.name) })),
    ...(labels || []).map(l => ({ name: `_${l.name}`, displayName: l.displayName, dataType: l.labelType })),
  ]

  const needsLoading = useMemo(() => {
    return !task._isFetched
  }, [task, task._isFetched])

  const taskTitle = needsLoading || !task.taskData ? '' : taskDisplayColumns
    .filter(x => taskIdColumns.includes(x.name))
    .map(c => `${c.displayName} ${task.taskData[c.name]}`)
    .join(', ')

  const bulkLoadTasks = async (actId, ids) => {
    if (bulkUpdateController.current) {
      bulkUpdateController.current.abort()
      bulkUpdateController.current = null
      dispatch(actionHubFetchSelectedTasks(false))
      if (ids.length > 0) {
        dispatch(actionHubFetchSelectedTasks(true))
      }
      dispatch(actionHubSelectedFetchedCheck(ids))
    }

    dispatch(actionHubFetchSelectedTasks(true))
    const controller = new AbortController()
    bulkUpdateController.current = controller

    dispatch(actionHubFetchingBulkTaskData(actId, ids))
    return bulkFetchTasks(actId, ids, 0, { signal: controller.signal })
      .then(({ data: responseData }) => {
        const { tasks: tasksToUpdate } = responseData
        const cleanedTasksInfo = tasksToUpdate.reduce((acc, t) => {
          acc[t.taskId] = {
            labels: t.labels || [],
            taskData: t.data || [],
            ...t.meta,
          }
          return acc
        }, {})
        dispatch(actionHubUpdateBulkTasks(actId, ids, cleanedTasksInfo))
        
        if (allowComments) {
          return bulkFetchTaskComments(actId, ids, { signal: controller.signal })
            .then(({ data: comments }) => {
              const uniqueUserIds = [...new Set(comments.map(c => c.userId))]

              dispatch(actionHubFetchingCommentAuthors())

              Promise.all(
                uniqueUserIds.map(userId => getUserById(userId)
                  .then(({ data }) => {
                    dispatch(actionHubFetchedCommentAuthor(userId, data))
                  })
                  .catch((e) => {
                    showNotification('Failed to fetch comment author', e, 'error')
                    return null
                  })),
              )

              dispatch(actionHubFetchedBulkTaskComments(actId, ids, comments))
            })
        }
        return Promise.resolve()
      })
  }

  const handleBulkFetchTaskError = (e, aId, tIds) => {
    if (e.name === 'AbortError' || e.message === 'canceled' || e.name === 'CanceledError') {
      return
    }

    const isHttpError = e.response && (e.response.status === 404 || e.response.status === 500)
    const errorMessage = 'Failed to load task data, please refresh the page and contact support if issues persist.'

    if (isHttpError) {
      showNotification(errorMessage, e, 'error')
      dispatch(push(`/action/${aId}`))

    } else {
      showNotification(errorMessage, e, 'error')
      dispatch(actionHubBulkFetchedTaskDataNoData(aId, tIds))
      dispatch(push(`/action/${aId}`))
    }

    dispatch(actionHubFetchSelectedTasks(false))
    dispatch(actionHubSelectedFetchedCheck(tIds))
  }

  const bulkSelectFetch = (aId, tIds) => {
    bulkLoadTasks(aId, tIds)
      .then(() => {
        dispatch(actionHubFetchSelectedTasks(false))
        dispatch(actionHubSelectedFetchedCheck(tIds))
      })
      .catch(e => handleBulkFetchTaskError(e, aId, tIds))
  }

  const loadSingleTask = (aId, tIds) => {
    const tId = tIds[0]
    return bulkLoadTasks(aId, [tId])
      .then(() => {
        if (isChildDataLevel) {
          return getTaskData(aId, tId, 0)
        }
        dispatch(actionHubFetchedTaskDataNoData(aId, tId))
        return Promise.resolve()
      })
      .then((taskDataResponse) => {
        if (taskDataResponse && taskDataResponse.data) {
          dispatch(actionHubFetchedTaskData(aId, tId, taskDataResponse.data))
        }
      })
      .then(() => {
        dispatch(actionHubFetchSelectedTasks(false))
      })
      .catch((e) => {
        handleBulkFetchTaskError(e, aId, [tId])
      })
  }

  useEffect(() => {
    if (Array.isArray(taskIds) && taskIds.length && !bulkUpdateModalOpenStatus && !isSubmittingLabels) {
      bulkSelectFetch(actionSetId, taskIds)
    } else if (needsLoading) {
      loadSingleTask(actionSetId, [taskId])
    }
  }, [taskIds, taskId, bulkUpdateModalOpenStatus])

  const setBulkUpdateLabelValue = (labelId, value) => {
    if (taskIds && tasks) {
      dispatch(actionHubUpdateMultipleTaskLabelValues(actionSetId, taskIds, labelId, value))
    }
  }

  const fetchNextPage = () => {
    if (task.pagination.hasNext && !task._isFetching) {
      dispatch(actionHubFetchingTaskData(actionSetId))
      getTaskData(actionSetId, taskId, task.pagination.page + 1)
        .then(({ data: responseData }) => {
          dispatch(actionHubFetchedTaskData(
            actionSetId,
            taskId,
            responseData,
          ))
        })
        .catch((e) => {
          showNotification('Could not load more data, please refresh the page and contact support if issues persist.', e, 'error')
        })
    }
  }

  const goToTaskList = () => {
    dispatch(push(`/action/${actionSetId}`))
  }

  const setLabelValue = (labelId, value) => {
    if (taskIds && tasks) {
      taskIds.forEach((id) => {
        dispatch(actionHubUpdateTaskLabelValue(actionSetId, id, labelId, value))
      })
    } else {
      dispatch(actionHubUpdateTaskLabelValue(actionSetId, taskId, labelId, value))
    }
  }

  const validateLabel = (labelId, error = null) => {
    if (taskIds && tasks) {
      taskIds.forEach((id) => {
        dispatch(validateTaskLabel(actionSetId, id, labelId, error))
      })
    } else {
      dispatch(validateTaskLabel(actionSetId, taskId, labelId, error))
    }
  }

  const resetLabelsToDefault = () => {
    dispatch(resetTaskLabelsToDefault(taskId))
  }

  const afterLabelsUpdated = (data, isBulkUpdate) => {
    let successText = 'Review findings saved'
    let taskList = [taskId]

    if (isBulkUpdate) {
      dispatch(actionHubFetchSelectedTasks(false))
      successText = 'Bulk updates saved'
      taskList = taskIds
      bulkSelectFetch(actionSetId, taskIds)
    }
    dispatch(submittedTaskLabels(actionSetId, taskList, jobId, {}))
    dispatch(submittedTaskStatus(actionSetId, taskList, data.statusId))
    updateGlobalFilters()
    showNotification(successText)
  }

  const labelUpdateErrorResponse = (e) => {
    if (e.response && e.response.status === 400) {
      const errors = e.response.data.result.info.message
        .split(';')
        .map((error) => {
          const components = error.split('|')
          return {
            labelId: components[0],
            error: components[1],
          }
        })

      errors.forEach((error) => {
        dispatch(validateTaskLabel(actionSetId, taskId, error.labelId, error.error))
      })

      dispatch(submittingTaskLabelsFailed(actionSetId, taskId))
      showNotification('Some values were invalid, please review the form and try again.', e, 'error')
    } else {
      showNotification('Could not save changes, please refresh the page and contact support if issues persist.', e, 'error')
    }
    if (taskIds.length && tasks.length) {
      taskIds.forEach(()=>dispatch(submittingTaskLabelsFailed(actionSetId, taskIds)))
    } else {
      dispatch(submittingTaskLabelsFailed(actionSetId, taskId))
    }
  }

  const updateDownloadStatus = async () => {
    const currentJobDownloadName = currentJob.downloads[0].name
    putUpdateDownloadStatus(jobId, currentJobDownloadName, downloadStatuses.notStarted, isUserExternal)
      .then(() => {
        dispatch(updateJobDownload(jobId, currentJobDownloadName, { status: downloadStatuses.notStarted }))
      })
      .catch(() => {
        dispatch(notify('Failed to update download status; please contact support', 'error'))
      })
  }

  const updateTaskDetails = (updatedData) => {
    dispatch(actionHubUpdatingTaskDetails(true))
    putTaskDetails(actionSetId, taskId, updatedData)
      .then(({ data }) => {
        dispatch(actionHubUpdateTaskDetails(actionSetId, taskId, data))
        dispatch(actionHubUpdatingTaskDetails(false))
        dispatch(notify('Task details updated'))
        if (currentJob.downloads.length > 0) {
          updateDownloadStatus()
        }
      })
      .catch((e) => {
        dispatch(actionHubUpdatingTaskDetails(false))
        showNotification('Could not save changes, please refresh the page and contact support if issues persist.', e, 'error')
      })
  }

  const submitTaskLabelsBulkUpdate = async (modalId) => {
    try {
      dispatch(modalHide(modalId))
      dispatch(submittingTaskLabels(actionSetId))

      const selectedTasksToUpdate = selectedTasks.map(selectedTask => ({
        id: selectedTask._id,
        labels: tasks.find(i => i._id === selectedTask._id).labels.filter(label => label.value || label.labelType === 'boolean')
          .map(label => ({
            ...label,
            labelId: label.id,
            labelName: label.displayName,
          })),
      }))
      dispatch(actionHubFetchSelectedTasks(true))
      const { data } = await putLabelsBulkUpdate(actionSetId, 0, selectedTasksToUpdate)
      if (currentJob.downloads.length > 0) {
        await updateDownloadStatus()
      }

      afterLabelsUpdated(data, true)
      dispatch(actionHubUpdateSelectedTasks(actionSetId, []))

      const actionResult = await getAction(actionSetId)
      dispatch(actionHubHomeTaskMetaAction(actionSetId, actionResult.data))
    } catch (e) {
      dispatch(actionHubFetchSelectedTasks(false))
      labelUpdateErrorResponse(e)
    }
  }

  const submitTaskLabels = async () => {
    try {
      dispatch(submittingTaskLabels(actionSetId, taskId))

      const { data } = await putLabels(actionSetId, 0, taskId, task.labels)

      if (currentJob.downloads.length > 0) {
        await updateDownloadStatus()
      }

      dispatch(actionHubUpdatingTaskDetails(true))
      const { data: responseData } = await getTaskDetailsOnly(actionSetId, taskId, allowComments)
      dispatch(actionHubUpdateTaskDetails(actionSetId, taskId, responseData))
      dispatch(actionHubUpdatingTaskDetails(false))

      afterLabelsUpdated(data, false)

      const { data: refreshedMeta } = await getTaskMeta(actionSetId, taskId)
      dispatch(actionHubFetchedTaskMeta(actionSetId, taskId, refreshedMeta))
    } catch (e) {
      dispatch(actionHubFetchSelectedTasks(false))
      if (e.message === 'Failed to fetch task details') {
        showNotification('Could not load task details, please refresh the page and contact support if issues persist.', e, 'error')
      } else {
        labelUpdateErrorResponse(e)
      }
    } finally {
      dispatch(actionHubUpdatingTaskDetails(false))
    }
  }

  const setTaskActionState = async (previousTaskActionId, newTaskActionId) => {
    try {
      dispatch(submittedTaskActionState(actionSetId, [taskId], newTaskActionId))

      const isCommentReceived = newTaskActionId === 5
      const isCommentRequired = newTaskActionId === 4 || isCommentReceived
      dispatch(updateIsCommentRequired(taskId, isCommentRequired))
      dispatch(updateIsCommentReceived(taskId, isCommentReceived))

      const updatedData = {
        taskActionId: newTaskActionId,
        isCommentRequired,
        isCommentReceived,
      }
      await putTasksUpdate(actionSetId, [taskId], updatedData)
      updateGlobalFilters()
    } catch (e) {
      dispatch(submittedTaskActionState(actionSetId, [taskId], previousTaskActionId))
      const previousIsCommentRequired = previousTaskActionId === 4 || previousTaskActionId === 5
      const previousIsCommentReceived = previousTaskActionId === 5
      dispatch(updateIsCommentRequired(taskId, previousIsCommentRequired))
      dispatch(updateIsCommentReceived(taskId, previousIsCommentReceived))
      showNotification('Could not save changes, please refresh the page and contact support if issues persist.', e, 'error')
    }
  }

  const setTasksActionState = async (taskIdsToUpdate, newTaskActionId) => {
    try {
      dispatch(submittedTaskActionState(actionSetId, taskIdsToUpdate, newTaskActionId))

      const isCommentReceived = newTaskActionId === 5
      const isCommentRequired = newTaskActionId === 4 || isCommentReceived
      taskIdsToUpdate.forEach((id) => {
        dispatch(updateIsCommentRequired(id, isCommentRequired))
        dispatch(updateIsCommentReceived(id, isCommentReceived))
      })

      const updatedData = {
        taskActionId: newTaskActionId,
        isCommentRequired,
        isCommentReceived,
      }
      await putTasksUpdate(actionSetId, taskIdsToUpdate, updatedData)
      updateGlobalFilters()
    } catch (e) {
      taskIdsToUpdate.forEach((id) => {
        const previousTaskActionId = taskIdsToUpdate.find(i => i._id === id).taskActionId
        dispatch(submittedTaskActionState(actionSetId, [id], previousTaskActionId))
        const previousIsCommentRequired = previousTaskActionId === 4 || previousTaskActionId === 5
        const previousIsCommentReceived = previousTaskActionId === 5
        dispatch(updateIsCommentRequired(id, previousIsCommentRequired))
        dispatch(updateIsCommentReceived(id, previousIsCommentReceived))
      })
      showNotification('Could not save Action state, please refresh the page and contact support if issues persist.', e, 'error')
    }
  }

  const getAssignee = (assigneeType) => {
    switch (assigneeType) {
      case 'clientTeamId':
        return 'client'
      case 'reviewerId':
        return 'reviewer'
      default:
        return 'assignee'
    }
  }

  const setTaskAssignee = (userId, assigneeType) => {
    putTasksAssignee(actionSetId, [taskId], userId, assigneeType)
      .then(() => {
        dispatch(submittedTaskAssignee(actionSetId, [taskId], userId, assigneeType))
        updateGlobalFilters()
        showNotification(`Updated ${getAssignee(assigneeType)} for task`)
      })
      .catch((e) => {
        showNotification('Could not save changes, please refresh the page and contact support if issues persist.', e, 'error')
      })
  }

  const setTaskStatus = (statusId) => {
    putTasksStatus(actionSetId, [taskId], statusId)
      .then(() => {
        dispatch(submittedTaskStatus(actionSetId, [taskId], statusId))
        updateGlobalFilters()
        showNotification('Status updated')
      })
      .catch((e) => {
        showNotification('Could not save status, please refresh the page and contact support if issues persist.', e, 'error')
      })
  }

  const setTaskPriority = (priorityId) => {
    putTasksPriority(actionSetId, [taskId], priorityId)
      .then(() => {
        dispatch(submittedTaskPriority(actionSetId, [taskId], priorityId))
      })
      .catch((e) => {
        showNotification('Could not save priority, please refresh the page and contact support if issues persist.', e, 'error')
      })
  }

  const removeTaskComment = (commentId) => {
    deleteTaskComment(actionSetId, taskId, commentId)
      .then(({ data }) => {
        throw new Error(`To be implemented! ${data}`)
      })
  }

  const setTasksCommentStatus = async (selectedIds, commentStatusId) => {
    dispatch(submittedTaskCommentStatus(actionSetId, selectedIds, commentStatusId))

    await putTasksCommentStatus(actionSetId, selectedIds, commentStatusId)
      .catch((e) => {
        selectedIds.forEach((id) => {
          const currentTask = allTasks[id]
          const previousCommentStatusId = currentTask.commentStatusId
          dispatch(submittedTaskCommentStatus(actionSetId, [id], previousCommentStatusId))
        })
        showNotification('Could not save comment status, please refresh the page and contact support if issues persist.', e, 'error')
      })
  }

  const setTaskCommentStatus = async (commentStatusId) => {
    await setTasksCommentStatus([taskId], commentStatusId)
  }

  const submitTaskComment = async (comment, fileNames, commentType) => {
    dispatch(submittingTaskComment(actionSetId, taskId))

    try {
      const { data: responseData } = await postTaskComment(actionSetId, taskId, comment, fileNames, commentType)

      dispatch(actionHubFetchingCommentAuthors())
      try {
        const { data: authorData } = await getUserById(responseData.userId)
        dispatch(actionHubFetchedCommentAuthor(responseData.userId, authorData))
      } catch (e) {
        showNotification('Failed to fetch comment author', e, 'error')
      }

      dispatch(submittedTaskComment(actionSetId, taskId, responseData))

      if ((commentType === 1 || commentType === 2) && !isUserExternal && task.commentStatusId === 0) {
        setTaskCommentStatus(1)
      }

      return responseData
    } catch (e) {
      dispatch(submittingTaskCommentFailed(actionSetId, taskId))
      showNotification('Could not save comment. Please try again or contact support if the issue persists.', e, 'error')
      throw e
    }
  }

  const removeAssignedTask = async () => {
    await dispatch(actionHubRemoveTask(parseInt(taskId, 10)))
  }

  const saveFindings = async (isBulkUpdate = false) => {
    try {
      let taskList = [taskId]
      if (!isBulkUpdate) {
        await submitTaskLabels()
      } else {
        await submitTaskLabelsBulkUpdate(MODAL_BULK_ACTION_UPDATES)
        taskList = taskIds
      }

      // Clear sign off
      await putTasksSignOffDetails(actionSetId, taskList, null, null)
      dispatch(updateSignOffDetails(taskList, null, null))
    } catch (e) {
      showNotification('Could not clear sign-off details, please refresh the page and try again.', e, 'error')
    }
  }
  return {
    data: task,
    // dataCount: task?.pagination?.totalItems,
    taskData,
    taskTitle,
    columns: mergedColumns,
    detailColumns: displayDetailColumns,
    meta,
    isFetched: !needsLoading && !isBulkUpdateLoading,
    goToTaskList,
    setLabelValue,
    submitTaskLabelsBulkUpdate,
    submitTaskLabels,
    validateLabel,
    setTaskActionState,
    setTasksActionState,
    setTaskAssignee,
    setTaskStatus,
    setTaskPriority,
    fetchNextPage,
    submitTaskComment,
    allowComments,
    allowAttachments,
    removeTaskComment,
    updateTaskDetails,
    setTaskCommentStatus,
    setTasksCommentStatus,
    removeAssignedTask,
    setBulkUpdateLabelValue,
    saveFindings,
    resetLabelsToDefault,
  }
}
